import React, { useMemo, useState } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faUsers, faUser } from '@fortawesome/pro-light-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { trpc } from '@/utils/trpc-client';
import { useWrkthruSwitcherEnabled } from '@/utils/feature-flags';
import { OutlineButton } from '@/components/display/button';
import { UserRoleBadges } from '@/components/display/role-badges';
import { filterUsersBySearch } from 'shared/src/utils/user/user-utils';
import type { ViewAs, WrkthruSwitcherOptions } from 'shared/src/trpc-types/daily-brief-trpc-types';

type SwitcherUser = WrkthruSwitcherOptions['users'][number];
type SwitcherTeam = WrkthruSwitcherOptions['teams'][number];

// Cap the unfiltered list; the search input narrows it before this bites.
const MAX_ROWS = 200;

type Props = {
  value: ViewAs | undefined;
  onChange: (viewAs: ViewAs | undefined) => void;
};

export function WrkthruSwitcher({ value, onChange }: Props) {
  const enabled = useWrkthruSwitcherEnabled();
  const { data, isPending } = trpc.wrkthruSwitcherOptions.useQuery(undefined, { enabled });
  if (!enabled) return null;
  return (
    <div className="flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <WrkthruSwitcherView
        users={data?.users ?? []}
        teams={data?.teams ?? []}
        isLoading={isPending}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}

type ViewProps = Props & {
  users: SwitcherUser[];
  teams: SwitcherTeam[];
  isLoading: boolean;
};

// Exported for Storybook, which renders it without the tRPC provider.
export function WrkthruSwitcherView({ users, teams, isLoading, value, onChange }: ViewProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-slate-600">Viewing:</span>

      <SwitcherMenu
        config={USER_MENU}
        items={users}
        isLoading={isLoading}
        selectedId={value?.kind === 'user' ? value.userId : undefined}
        onSelect={user => onChange({ kind: 'user', userId: user.id })}
      />

      <SwitcherMenu
        config={TEAM_MENU}
        items={teams}
        isLoading={isLoading}
        selectedId={value?.kind === 'team' ? value.teamId : undefined}
        onSelect={team => onChange({ kind: 'team', teamId: team.id })}
      />

      {value ? (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="text-sm text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline">
          Reset to me
        </button>
      ) : null}
    </div>
  );
}

// Static description of one menu variant. Everything per-kind lives here so
// SwitcherMenu instances only take live data (items, loading, selection).
type SwitcherMenuConfig<T extends { id: string }> = {
  noun: string;
  icon: IconDefinition;
  emptyLabel: string;
  labelOf: (item: T) => string;
  panelWidth: string;
  filter: (items: T[], query: string) => T[];
  renderRow: (item: T) => React.ReactNode;
};

const USER_MENU: SwitcherMenuConfig<SwitcherUser> = {
  noun: 'users',
  icon: faUser,
  emptyLabel: 'By user',
  labelOf: user => user.name,
  panelWidth: 'w-80',
  filter: filterUsersBySearch,
  renderRow: user => (
    <>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-slate-800">{user.name}</span>
        <span className="truncate text-xs text-slate-500">{user.email}</span>
      </span>
      <UserRoleBadges roles={user.roles} theme="light" />
    </>
  )
};

const TEAM_MENU: SwitcherMenuConfig<SwitcherTeam> = {
  noun: 'teams',
  icon: faUsers,
  emptyLabel: 'By team',
  labelOf: team => team.name,
  panelWidth: 'w-64',
  filter: filterTeamsBySearch,
  renderRow: team => <span className="truncate text-slate-800">{team.name}</span>
};

function SwitcherMenu<T extends { id: string }>({
  config,
  items,
  isLoading,
  selectedId,
  onSelect
}: {
  config: SwitcherMenuConfig<T>;
  items: T[];
  isLoading: boolean;
  selectedId: string | undefined;
  onSelect: (item: T) => void;
}) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(
    () => config.filter(items, query).slice(0, MAX_ROWS),
    [config, items, query]
  );
  const selected = selectedId ? items.find(item => item.id === selectedId) : undefined;
  const label = selected ? config.labelOf(selected) : config.emptyLabel;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton as="div">
        <OutlineButton selected={!!selectedId} onClick={() => {}}>
          <FontAwesomeIcon icon={config.icon} />
          <span className="max-w-48 truncate whitespace-nowrap">{label}</span>
          <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
        </OutlineButton>
      </MenuButton>

      <MenuItems
        // z-20 to clear the sticky section headers (z-10)
        className={`absolute left-0 z-20 mt-2 ${config.panelWidth} origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none`}>
        <div className="border-b border-slate-100 p-2">
          <input
            type="text"
            value={query}
            autoFocus
            placeholder={isLoading ? `Loading ${config.noun}…` : `Search ${config.noun}…`}
            onChange={e => setQuery(e.target.value)}
            // Suppress only the Menu's type-ahead (printable keys) and Home/End
            // item jumps so typing stays in the input. Escape/arrows/Enter/Tab
            // must keep propagating — Headless UI handles them via keydown from
            // the focused descendant, so stopping them kills keyboard nav.
            onKeyDown={e => {
              if (e.key.length === 1 || e.key === 'Home' || e.key === 'End') e.stopPropagation();
            }}
            className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">
              {isLoading ? 'Loading…' : `No ${config.noun}`}
            </div>
          ) : (
            filtered.map(item => (
              <MenuItem key={item.id}>
                <button
                  type="button"
                  onClick={() => onSelect(item)}
                  className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100 data-focus:bg-slate-100 ${
                    item.id === selectedId ? 'bg-slate-50' : ''
                  }`}>
                  {config.renderRow(item)}
                </button>
              </MenuItem>
            ))
          )}
        </div>
      </MenuItems>
    </Menu>
  );
}

function filterTeamsBySearch(teams: SwitcherTeam[], query: string): SwitcherTeam[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return teams;
  return teams.filter(t => t.name.toLowerCase().includes(needle));
}
